import { Injectable } from '@angular/core';
import { StripeAccount, StripeAccountDetails } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service';
import { firstValueFrom, map, Observable } from 'rxjs';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service used to get and save stripe account data.
 */
@Injectable()
export class StripeAccountService {
    private readonly stripeAccountsBaseUrl: string;

    constructor(private http: HttpServiceHelper) {
        this.stripeAccountsBaseUrl = `${this.http.configService.getApiBaseUrl()}/stripe-accounts/`;
    }

    /**
     * Get all stripe accounts based on the query parameters
     */
    public getIndex(params: string[]): Observable<StripeAccount[]> {
        let url: string = this.stripeAccountsBaseUrl;
        url += this.http.getQueryParameters(null, params);

        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;
                const accounts: StripeAccount[] = [];

                for (const acct of json.data) {
                    accounts.push((new StripeAccount()).deserialize(acct));
                }

                return accounts;
            }));
    }

    /**
     * Get a single stripe account. The populateIfEmpty parameter tells the service to populate the data from
     * stripe if it's empty. This will be used to auto backfill custom stripe accounts previously created.
     *
     * @param stripeAccountId
     * @param populateIfEmpty
     */
    public get(stripeAccountId: string, populateIfEmpty: boolean): Observable<StripeAccount> {
        let url: string = `${this.stripeAccountsBaseUrl}${stripeAccountId}`;

        if (populateIfEmpty) {
            url += this.http.getQueryParameters(null, ['populateIfEmpty=1']);
        }

        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => new StripeAccount().deserialize(response)));
    }

    /**
     * Transfer funds to the event's configured stripe managed account's bank account
     */
    public payoutFunds(eventId: string): Observable<number> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/payout-funds`;

        return this.http.post(apiRequestUrl, null)
            .pipe(map((response: IJsonApiResponse) => response['payoutTotal']));
    }

    /**
     * Get the event Stripe account details
     */
    public getStripeAccountDetails(eventId: string, displaySpinner: boolean = true): Observable<StripeAccountDetails> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/stripe-account`;

        return this.http.get(apiRequestUrl, displaySpinner)
            .pipe(map((response: IJsonApiResponse) => (new StripeAccountDetails()).deserialize(response)));
    }

    /**
     * Get the net balance from stripe for only this event
     */
    public getEventNetBalance(eventId: string): Observable<number> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/stripe-net-balance`;

        return this.http.get<IEventNetBalance>(apiRequestUrl, false)
            .pipe(map((response: IEventNetBalance) => response.netAmount));
    }

    /**
     * Get a generated temporary url a user can use to access the Stripe express dashboard
     */
    public getExpressDashboardUrl(eventId: string, stripeAccountId: string): Observable<IStripeExpressDashboardUrl> {
        const params = new URLSearchParams({
            stripeAccountId: stripeAccountId,
            refreshUrl: this.getRefreshUrl(eventId),
            returnUrl: this.getReturnUrl(eventId),
        });
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/stripe-dashboard-url?${params.toString()}`;

        return this.http.get<IStripeExpressDashboardUrl>(apiRequestUrl);
    }

    public async createExpressAccount(eventId: string): Promise<IStripeCreateAccount> {
        const apiRequestUrl: string = `${this.getApiUrl(eventId, null)}/create-stripe-account`;

        const resp = await firstValueFrom(this.http.post<IStripeCreateAccount>(
            apiRequestUrl,
            {
                refreshUrl: this.getRefreshUrl(eventId),
                returnUrl: this.getReturnUrl(eventId),
            },
        ));

        return resp;
    }

    private getRefreshUrl(eventId: string): string {
        const url = new URL(window.location.href);
        url.pathname = '/login/stripe-express-redirect';
        url.searchParams.set('eventId', eventId);
        return url.toString();
    }

    private getReturnUrl(eventId: string): string {
        const url = new URL(window.location.href);
        url.pathname = `/event/${eventId}/setup/bank-account`;
        return url.toString();
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, includes: string[] = null, queryParams: string[] = null): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/`;

        if (eventId) {
            url += eventId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

export interface IEventNetBalance {
    netAmount: number;
}

export interface IStripeExpressDashboardUrl {
    isSetupComplete: boolean;
    url: string;
}

export interface IStripeCreateAccount {
    stripeAccount: StripeAccount,
    onboardUrl: string,
}
